import { type FC, type ReactNode } from "react";

import { Button, type Status } from "@snapchat/snap-design-system";

import icon from "@app-assets/png/lens-plus-border.png";
import addPreviewIconSvg from "@app-assets/svg/add-preview-icon.svg";

import { type SurfaceType } from "../../gql-generated/types";
import { Flexbox } from "../../shared/components";
import { type LensPreviewMediaItem, defaultPreviewMediaItem, getItemId } from "../../shared/state";
import { UpdateMessage } from "../../submit/components/lens-details/shared/UpdateMessage";
import { type PreviewAspectRatio, Scale } from "../models";

import { AddPreviewButton, IconContainer, IconElement } from "./LensPreview.styles";
import { actionButtonStyle } from "./LensPreviewSpecs.styles";
import styles from "./LensPreviewsViewer.module.css";
import { PreviewViewerWithCTA } from "./PreviewViewerWithCTA";
import { LensPreviewUpdatedInLensStudio } from "./messages.generated";
export interface LensPreviewsViewerProps {
    previewMedia: LensPreviewMediaItem[];
    badgeText?: ReactNode;
    badgeStatus?: Status;
    aspectRatio?: PreviewAspectRatio;
    surface?: SurfaceType;
    autoplay?: boolean;
    hideActionButton?: boolean;
    shouldShowUpdateMessage?: boolean;
    buttonAction: () => void;
    buttonText: ReactNode;
    logoSrc?: string;
    disabled?: boolean;
    isReadOnly?: boolean;
    showModal?: () => void;
    maxNumPreviews: number;
    isExclusive?: boolean;
}

export const LensPreviewsViewer: FC<LensPreviewsViewerProps> = ({
    previewMedia,
    badgeText,
    badgeStatus,
    aspectRatio,
    autoplay,
    hideActionButton,
    shouldShowUpdateMessage = false,
    buttonAction,
    buttonText,
    logoSrc,
    disabled,
    isReadOnly,
    maxNumPreviews,
    surface,
    showModal,
    isExclusive,
}) => {
    return (
        <Flexbox flexDirection="column" alignItems="stretch" gap="10px">
            <PreviewViewerWithCTA
                scale={Scale.Percent100}
                isVideo={(previewMedia[0] ?? defaultPreviewMediaItem).isVideo}
                badgeText={badgeText}
                badgeStatus={badgeStatus}
                src={previewMedia[0]?.url}
                autoplay={autoplay}
                aspectRatio={aspectRatio}
                surfaceType={surface}
                isReadOnly={isReadOnly}
                disabled={disabled}
                showModal={showModal}
            />
            {logoSrc && (
                <IconContainer>
                    <IconElement cursor={isReadOnly ? "default" : "pointer"} src={logoSrc} />
                    {isExclusive && <img className={styles.lensPlusBorder} src={icon} alt="lens-plus-border" />}
                    {!disabled && !isReadOnly && <AddPreviewButton onClick={buttonAction} src={addPreviewIconSvg} />}
                </IconContainer>
            )}
            <Flexbox flexDirection="row" justifyContent="space-between">
                {Array.from({ length: maxNumPreviews - 1 }).map((_, index) => {
                    const mediaIndex = index + 1;
                    const currentPreview = previewMedia[mediaIndex] ?? defaultPreviewMediaItem;
                    return (
                        <PreviewViewerWithCTA
                            key={getItemId(currentPreview) + `-${mediaIndex}`}
                            scale={Scale.Percent30}
                            isVideo={currentPreview.isVideo}
                            src={currentPreview.url}
                            autoplay={autoplay}
                            aspectRatio={aspectRatio}
                            isEmpty={currentPreview.type === "default"}
                        />
                    );
                })}
            </Flexbox>
            <Flexbox flexDirection="column" alignItems={shouldShowUpdateMessage ? "flex-start" : "center"}>
                {shouldShowUpdateMessage && (
                    <UpdateMessage displayLabel={false}>
                        <LensPreviewUpdatedInLensStudio />
                    </UpdateMessage>
                )}
                {!hideActionButton && (
                    <Button type="link" onClick={buttonAction} className={actionButtonStyle} isDisabled={disabled}>
                        {buttonText}
                    </Button>
                )}
            </Flexbox>
        </Flexbox>
    );
};
